import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectAvatar, updateGameState, fetchGameState } from '@/store/gameSlice';
import { actionMessage, avatarTaken, selectionClosed } from '@/game/actionError';
import { playNotAllowed } from '@/audio/notAllowed';
import { selectUser } from '@/store/authSlice';
import { ProfileMenu } from '@/components/ProfileMenu/ProfileMenu';
import { AvatarSelectionGrid } from '@/components/Avatar/AvatarSelectionGrid';
import { SelectionTimer } from '@/components/Avatar/SelectionTimer';
import { sseManager } from '@/services/sse';
import type { AppDispatch } from '@/store';
import type { SSEMessage } from '@/services/sse';
import './AvatarSelection.css';

export function AvatarSelectionPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const { currentGame: gameState, error } = useSelector((state: any) => state.game);

  const [selectingAvatarId, setSelectingAvatarId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);

  // Auto-navigate to game when phase is beyond avatar selection
  // This includes: start_game, land_claim, development, production, auction, etc.
  useEffect(() => {
    console.log(`🔍 Phase check: gameState?.phase = ${gameState?.phase}, gameId = ${gameId}`);
    
    // Phases that should stay on avatar selection page
    const avatarSelectionPhases = ['lobby', 'name_selection', 'avatar_selection', 'ready_to_start'];
    
    if (gameState?.phase && !avatarSelectionPhases.includes(gameState.phase) && gameId) {
      console.log(`🎬 Phase is ${gameState.phase} (beyond avatar selection)! Navigating to game...`);
      navigate(`/game/${gameId}`);
    }
  }, [gameState?.phase, gameId, navigate]);

  // Fetch game state on mount
  useEffect(() => {
    if (gameId) {
      dispatch(fetchGameState(gameId)).then((result) => {
        if (fetchGameState.rejected.match(result)) {
          // Game doesn't exist - redirect to lobby
          console.error('❌ Game not found, redirecting to lobby');
          navigate('/lobby', { replace: true });
        } else {
          setIsGenerating(false);
        }
      });
    }
  }, [gameId, dispatch, navigate]);

  // Check if all avatars are selected
  const allAvatarsSelected =
    gameState?.avatars?.every((a: any) => a.selectedBy !== null) || false;

  // Polling: Refresh game state every 1 second when all avatars selected or in ready phase, 3 seconds otherwise
  useEffect(() => {
    // Stop polling if we've transitioned to start_game or land_claim phase
    if (!gameId || !gameState || gameState.phase === 'start_game' || gameState.phase === 'land_claim') return;

    console.log('🔄 Starting polling for avatar updates...');

    // The server can assign the rest and leave this phase within a few seconds.
    // A 3s poll left the picker up after selection had closed.
    const pollIntervalMs = (gameState.phase === 'ready_to_start' || allAvatarsSelected) ? 500 : 1000;

    const pollInterval = setInterval(() => {
      console.log('📊 Polling for game state update...');
      dispatch(fetchGameState(gameId));
    }, pollIntervalMs);

    return () => {
      console.log('🛑 Stopping polling');
      clearInterval(pollInterval);
    };
  }, [gameId, gameState?.phase, allAvatarsSelected, dispatch]); // Added allAvatarsSelected dependency

  // Setup SSE listeners for real-time updates
  useEffect(() => {
    if (!gameId || !gameState || !user?.userId) return;

    // Connect to SSE for real-time updates
    sseManager.connect(gameId, user.userId).catch((error) => {
      console.warn('⚠️  SSE connection failed, using polling fallback:', error);
    });

    // Handle SSE messages
    const handleMessage = (message: SSEMessage) => {
      console.log('📨 SSE message:', message.type);

      switch (message.type) {
        case 'GAME_STATE_UPDATE':
          if (message.gameState) {
            dispatch(updateGameState(message.gameState as any));
            // If avatars are generated, stop generating state
            if (message.gameState.avatars && message.gameState.avatars.length > 0) {
              setIsGenerating(false);
            }
          }
          break;

        case 'PHASE_CHANGE':
          if (message.gameState) {
            console.log(`🎉 Phase changed via SSE: ${message.oldPhase} → ${message.newPhase}`);
            dispatch(updateGameState(message.gameState as any));
          }
          break;

        default:
          console.log('📨 Unhandled SSE message type:', message.type);
          break;
      }
    };

    const unsubscribe = sseManager.onMessage(handleMessage);

    return () => {
      unsubscribe();
      sseManager.disconnect();
    };
  }, [gameId, gameState, user?.userId, dispatch]);

  const handleSelectAvatar = async (avatarId: string) => {
    if (!gameId) return;

    setSelectingAvatarId(avatarId);

    try {
      const result = await dispatch(
        selectAvatar({
          gameId,
          avatarId,
        })
      );

      if (selectAvatar.fulfilled.match(result)) {
        console.log('Avatar selected successfully');
      } else if (selectAvatar.rejected.match(result)) {
        const errorMessage = actionMessage(result, 'Failed to select avatar');

        if (avatarTaken(errorMessage)) {
          playNotAllowed();
        } else if (selectionClosed(errorMessage) && gameId) {
          // The server has already moved on. Refresh and let the phase effect open the board.
          dispatch(fetchGameState(gameId));
        } else {
          console.error('Avatar selection rejected:', result);
          alert(`Error: ${errorMessage}`);
        }
      }
    } catch (err: any) {
      console.error('Error selecting avatar:', err);
      alert(`Error: ${err.message || 'Failed to select avatar'}`);
    } finally {
      setSelectingAvatarId(null);
    }
  };

  const seated = gameState?.players.find(
    (p: { userId?: string; playerId?: string; username?: string; playerName?: string }) => p.userId === user?.userId || p.playerId === user?.userId
  );
  const currentPlayerName = seated?.playerName || user?.name || seated?.username || 'Player';

  return (
    <div className="avatar-selection-page">
      <header className="avatar-header">
        <h1>R.U.S.H.</h1>
        <div className="user-info">
          <span>Welcome, {currentPlayerName}</span>
          <ProfileMenu />
        </div>
      </header>

      <main className="avatar-main">
        <div className="avatar-container">
          {/* Generating Phase */}
          {isGenerating && (
            <div className="generating-phase">
              <div className="spinner"></div>
              <h2>Generating Avatars...</h2>
              <p>Please wait while we create your avatar options</p>
            </div>
          )}

          {/* Selection Phase */}
          {!isGenerating && gameState?.avatars && gameState?.phase !== 'ready_to_start' && (
            <>
              <h2 className="avatar-title">Select Your Avatar</h2>

              {/* Selection Timer */}
              {gameState.selectionTimer && (
                <SelectionTimer
                  timeRemaining={gameState.selectionTimer.timeRemaining}
                  totalDuration={gameState.selectionTimer.duration}
                />
              )}

              {/* Avatar Selection Grid */}
              <AvatarSelectionGrid
                avatars={gameState.avatars}
                players={gameState.players}
                currentPlayerId={user?.userId || ''}
                onSelectAvatar={handleSelectAvatar}
                isLoading={selectingAvatarId !== null}
                selectedAvatarId={selectingAvatarId}
              />
            </>
          )}

          {/* Ready to Start Phase - Show after all avatars selected */}
          {!isGenerating && gameState?.phase === 'ready_to_start' && (
            <div className="all-ready-message">
              <h3>🚀 All Players Ready!</h3>
              <p>Starting game...</p>
            </div>
          )}

          {error && <div className="error-message">Error: {error}</div>}
        </div>
      </main>
    </div>
  );
}

